import React, { PureComponent } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  asyncRequestContextsSelectors,
  asyncRequestContextsActions,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/asyncRequestContexts';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Trash } from '@lifeomic/chromicons';
import { surveysActions } from '../../../../../redux/modules/surveys';
import { contextKeys } from '../../../../../redux/modules/asyncRequestContexts';
import NotificationHelper from '@lifeomic/phc-web-toolkit/src/redux/util/NotificationHelper';
import EnforceABAC, {
  permissions,
} from '@lifeomic/phc-web-toolkit/src/components/EnforceABAC';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { Button } from '@lifeomic/chroma-react/components/Button';

const messages = defineMessages({
  title: {
    id: 'patientDetail.surveys.response.deleteDialog.title',
    defaultMessage: 'Delete Survey Response?',
  },
  content: {
    id: 'patientDetail.surveys.response.deleteDialog.content',
    defaultMessage:
      'Are you sure you want to delete this response for survey "{surveyName}" from {surveyDate}?  This can not be undone.',
  },
  acceptButtonText: {
    id: 'patientDetail.surveys.response.deleteDialog.accept',
    defaultMessage: 'Delete',
  },
  cancelButtonText: {
    id: 'patientDetail.surveys.response.deleteDialog.cancel',
    defaultMessage: 'Cancel',
  },
  deletionSuccess: {
    id: 'patientDetail.surveys.response.deletionSuccess',
    defaultMessage: 'Successfully deleted the survey response',
  },
  deletionFailure: {
    id: 'patientDetail.surveys.response.deletionFailure',
    defaultMessage: 'Error occurred deleting the survey response',
  },
  deleteResponse: {
    id: 'patientDetail.surveys.response.deleteResponse',
    defaultMessage: 'Delete Response',
  },
});

class DeleteResponseButton extends PureComponent {
  state = {
    isDialogOpen: false,
  };

  handleDialogOpen = () => {
    this.setState({ isDialogOpen: true });
  };

  handleDialogClose = () => {
    this.setState({ isDialogOpen: false });
  };

  componentDidUpdate(prevProps) {
    if (prevProps.isFetching && !this.props.isFetching && !this.props.error) {
      this.props.onResponseDelete(this.props.responseId);
    }
  }

  handleDeleteConfirm = () => {
    const {
      deleteResponse,
      responseId,
      contextKey,
      intl: { formatMessage },
    } = this.props;

    const notification = new NotificationHelper(
      formatMessage(messages.deletionSuccess),
      formatMessage(messages.deletionFailure),
    );

    deleteResponse(responseId, contextKey, notification);
    this.handleDialogClose();
  };

  render() {
    const {
      isFetching,
      responseId,
      intl: { formatMessage },
      surveyName,
      surveyDate,
    } = this.props;

    if (!responseId) return null;

    return (
      <EnforceABAC permission={permissions.deleteData}>
        <>
          <IconButton
            aria-label={formatMessage(messages.deleteResponse)}
            aria-haspopup="dialog"
            aria-expanded={this.state.isDialogOpen}
            color="inverse"
            icon={Trash}
            disabled={isFetching}
            onClick={e => {
              e.stopPropagation();
              e.currentTarget.blur();
              this.handleDialogOpen();
            }}
          />
          <Modal
            isOpen={this.state.isDialogOpen}
            title={formatMessage(messages.title)}
            onDismiss={this.handleDialogClose}
            actions={
              <React.Fragment>
                <Button variant="text" onClick={this.handleDialogClose}>
                  {formatMessage(messages.cancelButtonText)}
                </Button>
                <Button variant="contained" onClick={this.handleDeleteConfirm}>
                  {formatMessage(messages.acceptButtonText)}
                </Button>
              </React.Fragment>
            }
          >
            <Text>
              {formatMessage(messages.content, { surveyName, surveyDate })}
            </Text>
          </Modal>
        </>
      </EnforceABAC>
    );
  }
}

DeleteResponseButton.propTypes = {
  responseId: PropTypes.string.isRequired,
  onResponseDelete: PropTypes.func.isRequired,
  surveyName: PropTypes.string.isRequired,
  surveyDate: PropTypes.string.isRequired,
};

const mapDispatchToProps = {
  resetContext: asyncRequestContextsActions.resetContext,
  deleteResponse: surveysActions.deleteSurveyResponse,
};

const mapStateToProps = (state, props) => {
  const contextKey = contextKeys.patientViewer.deleteSurveyResponse(
    props.responseId,
  );

  const { isFetchingSelector, errorSelector } =
    asyncRequestContextsSelectors.bindContextKeyToSelectors(contextKey);

  return {
    isFetching: isFetchingSelector(state),
    error: errorSelector(state),
    contextKey,
  };
};

export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
)(DeleteResponseButton);
